/**
 * La pop-up de campagne : quand l'ouvrir, quand la remontrer, quand se taire.
 *
 * La section a déjà tranché ce qui se sait au rendu (case cochée, période, page
 * permise). Ici se joue ce qui dépend du visiteur. Le principe : **une offre qu'on
 * connaît déjà ne se remontre pas**, et **on n'interrompt personne**. Le détail des
 * règles, et leur raison, est dans docs/23-pop-up-campagne.md.
 *
 * Première ouverture
 *   · au premier des signaux : temps passé sur la page, part de la page lue, souris
 *     qui quitte la fenêtre par le haut. Jamais avant 4 s ;
 *   · sur une fiche produit, seul le départ de la souris compte (réglable) ;
 *   · jamais par-dessus le bandeau de cookies, le panier, un menu, une autre fenêtre,
 *     ni pendant une saisie : elle attend que la voie soit libre ;
 *   · jamais tant que le code ou le cadeau est au panier, ni à qui a vu la landing ;
 *   · si la campagne le demande, jamais à un navigateur qui a déjà servi à commander.
 *
 * Réaffichage
 *   · jamais deux fois dans la même visite ;
 *   · plus jamais après un clic sur le bouton ou une copie du code ;
 *   · après une fermeture : attente de N jours, M affichages au plus ;
 *   · une fois, dès que le panier atteint le seuil sans le code (passe outre l'attente) ;
 *   · une dernière fois à partir du jour du dernier rappel (passe outre tout).
 *
 * La languette de rappel apparaît après une fermeture et rouvre la pop-up d'un clic.
 * La masquer, c'est dire « laissez-moi » : plus de rappel automatique, seuls restent
 * le seuil et le dernier rappel, qui apportent une information nouvelle.
 *
 * La mémoire tient dans le navigateur du visiteur (localStorage, sous le nom de la
 * campagne) : aucun cookie, rien d'envoyé nulle part. Dans l'éditeur de thème et en
 * aperçu (`?apercu-popup`), rien n'est écrit, et la languette paraît dès la fermeture.
 *
 * `?debug-popup` affiche sur la page chaque condition et son état, avec un bouton qui
 * remet la mémoire de ce navigateur à zéro : la réponse à « elle n'apparaît pas ».
 */

const JOUR = 86_400_000;
/** Aucun signal n'ouvre la pop-up avant ce délai : on laisse la page se montrer. */
const PLANCHER = 4000;
/** Après un obstacle (panier ouvert, bandeau…), la voie doit rester libre ce temps. */
const ACCALMIE = 1500;
/** Au-delà, on renonce pour cette page plutôt que de surgir à contretemps. */
const PATIENCE_MAX = 60_000;
const PREFIXE = 'akinod-popup:';
const SESSION = 'akinod-popup-session:';
/** Éditeur de thème : la pop-up était ouverte avant que la section se recharge. */
const EDITEUR_OUVERTE = 'akinod-popup-editeur:';
/** Le seuil peut rouvrir la pop-up dans la même visite, mais pas sur les talons d'une fermeture. */
const RECUL_SEUIL = 60_000;
/** Posée par `akinod-landing.js` sur chaque landing visitée, sans préfixe de langue. */
const LANDING = 'akinod-landing-vue:';
/**
 * Posée par la section pour un client connecté qui a déjà commandé, et par le pixel
 * « Akinod · déjà client » à la confirmation d'une commande. Lue telle quelle, sans
 * JSON : seule sa présence compte.
 */
const DEJA_CLIENT = 'akinod-deja-client';
const MARQUEUR_CADEAU = '_cadeau_offert';

/* ── Outils ───────────────────────────────────────────────────────────────────── */

function lire(stockage, cle) {
  try {
    return JSON.parse(window[stockage].getItem(cle));
  } catch {
    return null;
  }
}

function ecrire(stockage, cle, valeur) {
  try {
    window[stockage].setItem(cle, JSON.stringify(valeur));
  } catch {
    // Sans stockage (navigation privée stricte), la pop-up se remontrera : c'est
    // gênant, jamais faux.
  }
}

/** Le jour qu'il est à Paris, au format des réglages : les dates de campagne sont françaises. */
function jourAParis() {
  const parties = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const valeur = (type) => parties.find((p) => p.type === type)?.value;

  return `${valeur('year')}-${valeur('month')}-${valeur('day')}`;
}

function dejaClient() {
  try {
    return window.localStorage.getItem(DEJA_CLIENT) !== null;
  } catch {
    return false;
  }
}

const dateValide = (texte) => /^\d{4}-\d{2}-\d{2}$/.test(String(texte || '').trim());
const sansLangue = (chemin) => String(chemin).replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '') || '/';
const memeCode = (a, b) => String(a).toUpperCase() === String(b).toUpperCase();
const racine = () => window.Shopify?.routes?.root ?? '/';
const attendre = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

/** Pour l'analytique : un pixel (GA4, Meta…) peut s'abonner à ces événements. */
function publier(nom, donnees) {
  try {
    window.Shopify?.analytics?.publish?.(nom, donnees);
  } catch {
    // Une mesure qui échoue ne doit rien empêcher.
  }
}

async function lirePanier() {
  try {
    const reponse = await fetch(`${racine()}cart.js`, { headers: { Accept: 'application/json' } });

    return reponse.ok ? await reponse.json() : null;
  } catch {
    return null;
  }
}

function copierParSelection(texte) {
  const champ = document.createElement('textarea');
  champ.value = texte;
  champ.setAttribute('readonly', '');
  champ.style.position = 'fixed';
  champ.style.top = '0';
  champ.style.opacity = '0';
  document.body.append(champ);
  champ.select();
  champ.setSelectionRange(0, texte.length);
  let reussi = false;
  try {
    reussi = document.execCommand('copy');
  } catch {
    reussi = false;
  }
  champ.remove();

  return reussi;
}

async function copier(texte) {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(texte);

      return true;
    } catch {
      // voie de secours ci-dessous
    }
  }

  return copierParSelection(texte);
}

/* ── Ce qui empêche d'ouvrir maintenant ─────────────────────────────────────────── */

/**
 * Le bandeau tel qu'il est à l'écran. L'API `shouldShowBanner()` n'est pas consultée :
 * elle peut répondre oui sur une boutique dont le bandeau est désactivé, et la pop-up
 * attendrait alors un bandeau qui ne viendra jamais.
 */
function bandeauCookiesVisible() {
  const bandeau = document.getElementById('shopify-pc__banner');

  if (!bandeau || bandeau.getClientRects().length === 0) return false;

  const style = getComputedStyle(bandeau);

  return style.visibility !== 'hidden' && style.display !== 'none' && Number(style.opacity) > 0;
}

function autreFenetreOuverte(dialogue) {
  if ([...document.querySelectorAll('dialog[open]')].some((d) => d !== dialogue)) return true;
  if (document.querySelector('details[open].menu-drawer-container')) return true;

  // Les tiroirs d'Akinod — le menu (`#ak-drawer`, akinod-header.js) et les filtres
  // (`.ak-fdrawer`, akinod-collection.js) — ne sont ni des `dialog` ni le tiroir
  // d'Horizon testé au-dessus, qui n'est jamais monté ici. Ce qu'ils ont en commun,
  // c'est de bloquer le défilement du `body` le temps qu'ils sont ouverts : la
  // pop-up s'ouvrait donc par-dessus un menu déplié, ce que la règle « jamais
  // par-dessus une autre fenêtre » interdit justement. La pop-up, elle, pose son
  // verrou sur `<html>` : elle ne se voit pas elle-même dans ce test.
  if (document.body.style.overflow === 'hidden') return true;

  // Le verrou de défilement d'Horizon : un tiroir ou une fenêtre du thème est ouvert.
  return document.documentElement.hasAttribute('scroll-lock');
}

function saisieEnCours() {
  const actif = document.activeElement;

  if (!actif) return false;

  return actif.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(actif.tagName);
}

/* ── La pop-up ────────────────────────────────────────────────────────────────── */

class PopupCampagne {
  /**
   * @param {HTMLElement} racineSection
   * @param {HTMLDialogElement} dialogue
   * @param {Record<string, any>} brut
   */
  constructor(racineSection, dialogue, brut) {
    this.dialogue = dialogue;
    this.languette = racineSection.querySelector('[data-ak-pop-languette]');
    this.cfg = PopupCampagne.normaliser(brut);
    this.cle = PREFIXE + this.cfg.campagne;
    this.abandon = new AbortController();
    this.editeur = Boolean(window.Shopify?.designMode);
    this.apercu = new URLSearchParams(window.location.search).has('apercu-popup');
    this.diagnostic = new URLSearchParams(window.location.search).has('debug-popup') ? new Map() : null;
    this.offreAuPanier = false;
    this.verrouPose = false;
    this.idSection = racineSection.id.replace('shopify-section-', '');

    this.brancher();

    if (this.editeur) {
      if (lire('sessionStorage', EDITEUR_OUVERTE + this.idSection)) {
        this.precharger().then(() => this.ouvrir('editeur', 'editeur'));
      } else {
        this.majLanguette();
      }

      return;
    }

    if (this.apercu) {
      this.precharger().then(() => this.ouvrir('apercu', 'apercu'));

      return;
    }

    this.evaluer();
  }

  static normaliser(brut) {
    const nombre = (valeur, defaut) => (Number.isFinite(Number(valeur)) && valeur !== null && valeur !== '' ? Number(valeur) : defaut);

    return {
      campagne: String(brut.campagne || 'campagne').trim(),
      actif: brut.actif === true,
      debut: dateValide(brut.debut) ? brut.debut.trim() : '',
      fin: dateValide(brut.fin) ? brut.fin.trim() : '',
      relanceFinale: dateValide(brut.relanceFinale) ? brut.relanceFinale.trim() : '',
      delai: nombre(brut.delai, 12),
      defilement: nombre(brut.defilement, 40),
      sortie: brut.sortie !== false,
      menagerFiches: brut.menagerFiches !== false,
      repos: nombre(brut.repos, 3),
      maxAffichages: nombre(brut.maxAffichages, 3),
      relanceSeuil: brut.relanceSeuil !== false,
      exclureClients: brut.exclureClients === true,
      code: String(brut.code || '').trim(),
      seuil: nombre(brut.seuil, 0),
      varianteCadeau: nombre(brut.varianteCadeau, 0),
      codesPanier: Array.isArray(brut.codesPanier) ? brut.codesPanier.map(String) : [],
      cadeauAuPanier: brut.cadeauAuPanier === true,
      cible: String(brut.cible || ''),
      typePage: String(brut.typePage || ''),
    };
  }

  /* --- Mémoire ---------------------------------------------------------------- */

  get etat() {
    const lu = lire('localStorage', this.cle) || {};

    // Le code au panier comptait autrefois comme une offre prise pour de bon : un
    // testeur qui avait retiré le code ne revoyait plus jamais la pop-up.
    if (lu.conversion === 'panier') {
      lu.converti = false;
      delete lu.conversion;
    }

    return {
      vues: 0,
      derniere: 0,
      fermeture: 0,
      converti: false,
      seuil: false,
      finale: false,
      refus: false,
      ...lu,
    };
  }

  memoriser(changements) {
    if (this.editeur || this.apercu) return;
    ecrire('localStorage', this.cle, { ...this.etat, ...changements });
  }

  /** Ni l'éditeur ni l'aperçu ne comptent dans les mesures. */
  mesurer(nom, donnees = {}) {
    if (this.editeur || this.apercu) return;
    publier(nom, { campagne: this.cfg.campagne, ...donnees });
  }

  convertir(comment) {
    if (!this.etat.converti) this.memoriser({ converti: true, conversion: comment });
    this.majLanguette();
  }

  /* --- Conditions ------------------------------------------------------------- */

  dansLaPeriode(jour = jourAParis()) {
    const { actif, debut, fin } = this.cfg;

    return actif && !(debut && jour < debut) && !(fin && jour > fin);
  }

  landingVue() {
    if (!this.cfg.cible) return false;

    try {
      const chemin = sansLangue(new URL(this.cfg.cible, window.location.origin).pathname);

      return chemin !== '/' && lire('localStorage', LANDING + chemin) !== null;
    } catch {
      return false;
    }
  }

  offreDejaPrise({ codes, cadeau }) {
    return cadeau || (this.cfg.code !== '' && codes.some((c) => memeCode(c, this.cfg.code)));
  }

  /** Le motif d'un affichage automatique sur cette page, ou null. */
  motif(etat, jour) {
    const { relanceFinale, maxAffichages, repos } = this.cfg;

    if (etat.vues === 0) return 'premiere';
    if (relanceFinale && jour >= relanceFinale && !etat.finale) return 'finale';
    if (etat.refus || etat.vues >= maxAffichages) return null;

    const depuis = Date.now() - Math.max(etat.fermeture || 0, etat.derniere || 0);

    return depuis >= repos * JOUR ? 'rappel' : null;
  }

  seuilPossible(etat) {
    const { relanceSeuil, seuil, maxAffichages } = this.cfg;

    return relanceSeuil && seuil > 0 && !etat.seuil && etat.vues > 0 && etat.vues < maxAffichages;
  }

  /** Le montant qui compte pour le seuil : le panier sans le produit offert, comme `akinod-cadeau.js`. */
  atteintLeSeuil(panier) {
    const total = (panier?.items ?? [])
      .filter((ligne) => Number(ligne.variant_id) !== this.cfg.varianteCadeau)
      .reduce((somme, ligne) => somme + ligne.original_line_price, 0);

    return total >= this.cfg.seuil * 100;
  }

  etatDuPanier(panier) {
    return {
      codes: (panier?.discount_codes ?? []).map((c) => String(c.code)),
      cadeau: (panier?.items ?? []).some((ligne) => Boolean(ligne.properties?.[MARQUEUR_CADEAU])),
    };
  }

  /* --- Déroulé ---------------------------------------------------------------- */

  evaluer() {
    const jour = jourAParis();
    const { actif, debut, fin, campagne } = this.cfg;

    this.noter('Case « Afficher »', actif, actif ? 'cochée' : 'décochée');
    this.noter('Période', this.dansLaPeriode(jour), `aujourd'hui ${jour}, du ${debut || '…'} au ${fin || '…'}`);

    if (!this.dansLaPeriode(jour)) return;

    if (this.cfg.exclureClients) {
      const client = dejaClient();

      this.noter('Déjà client', !client, client ? 'ce navigateur a déjà commandé : elle se tait' : 'non');

      if (client) return;
    }

    this.offreAuPanier = this.offreDejaPrise({ codes: this.cfg.codesPanier, cadeau: this.cfg.cadeauAuPanier });
    this.noter('Offre au panier', !this.offreAuPanier, this.offreAuPanier ? 'code ou cadeau au panier : elle se tait' : 'non');

    if (!this.etat.converti && this.landingVue()) this.convertir('landing');

    const etat = this.etat;
    const session = Boolean(lire('sessionStorage', SESSION + campagne));

    this.noter('Offre déjà prise', !etat.converti, etat.converti ? `oui (${etat.conversion || '?'})` : 'non');
    this.noter('Mémoire', true, `${etat.vues} affichage(s)${etat.fermeture ? `, fermée le ${new Date(etat.fermeture).toLocaleString('fr-FR')}` : ''}${etat.refus ? ', languette masquée' : ''}`);
    this.noter('Cette visite', !session, session ? 'déjà montrée' : 'pas encore montrée');

    this.majLanguette();
    this.suivreLePanier();

    if (this.offreAuPanier || etat.converti || session) return;

    const motif = this.motif(etat, jour);
    const seuil = this.seuilPossible(etat);

    this.noter('Motif', Boolean(motif || seuil), motif || (seuil ? 'seulement si le panier atteint le seuil' : `aucun (repos de ${this.cfg.repos} j ou ${this.cfg.maxAffichages} affichages atteints)`));

    if (!motif && !seuil) return;
    if (motif) this.precharger();

    this.armer(motif);
  }

  /** Le panneau de `?debug-popup` : une ligne par condition, mise à jour à chaque étape. */
  noter(nom, ok, detail) {
    if (!this.diagnostic) return;

    this.diagnostic.set(nom, { ok, detail });

    let panneau = document.getElementById('ak-pop-diagnostic');

    if (!panneau) {
      panneau = document.createElement('div');
      panneau.id = 'ak-pop-diagnostic';
      panneau.style.cssText = 'position:fixed;top:12px;right:12px;z-index:2147483647;max-width:min(420px,calc(100vw - 24px));padding:12px 14px;background:rgba(17,17,17,.92);color:#ffffff;font:12px/1.5 ui-monospace,Menlo,monospace;border-radius:10px;box-shadow:0 10px 30px rgba(0,0,0,.3)';
      document.body.append(panneau);
    }

    const lignes = [...this.diagnostic].map(([cle, { ok: bon, detail: texte }]) => {
      const ligne = document.createElement('div');
      ligne.textContent = `${bon ? '✓' : '✗'} ${cle} : ${texte}`;
      ligne.style.color = bon ? '#ffffff' : '#ffb4b4';
      return ligne;
    });

    const titre = document.createElement('div');
    titre.textContent = `Pop-up « ${this.cfg.campagne} »`;
    titre.style.cssText = 'font-weight:bold;margin-bottom:6px';

    const bouton = document.createElement('button');
    bouton.type = 'button';
    bouton.textContent = 'Remettre ma mémoire à zéro';
    bouton.style.cssText = 'margin-top:8px;padding:4px 8px;font:inherit;color:#111111;background:#ffffff;border:0;border-radius:6px;cursor:pointer';
    bouton.addEventListener('click', () => {
      try {
        window.localStorage.removeItem(this.cle);
        window.sessionStorage.removeItem(SESSION + this.cfg.campagne);
        window.localStorage.removeItem(DEJA_CLIENT);
        Object.keys(window.localStorage).filter((k) => k.startsWith(LANDING)).forEach((k) => window.localStorage.removeItem(k));
      } catch {
        // rien à effacer
      }
      window.location.reload();
    });

    panneau.replaceChildren(titre, ...lignes, bouton);
  }

  /** Pose les signaux d'ouverture ; le premier qui se déclenche retire les autres. */
  armer(motif) {
    const { delai, defilement, sortie, menagerFiches, typePage } = this.cfg;
    const surFiche = menagerFiches && typePage === 'product';
    const signaux = new AbortController();
    const arrivee = performance.now();
    let parti = false;
    let reporte = false;

    const declencher = (source) => {
      if (parti) return;

      const reste = PLANCHER - (performance.now() - arrivee);

      if (reste > 0) {
        if (!reporte) window.setTimeout(() => { reporte = false; declencher(source); }, reste);
        reporte = true;

        return;
      }

      parti = true;
      signaux.abort();
      window.clearTimeout(minuteur);
      this.noter('Signaux', true, `parti : ${source}`);
      this.tenter(motif, source);
    };

    const options = { passive: true, signal: signaux.signal };
    this.abandon.signal.addEventListener('abort', () => signaux.abort(), { once: true });

    const minuteur = surFiche ? 0 : window.setTimeout(() => declencher('delai'), delai * 1000);

    this.noter('Signaux', true, surFiche
      ? 'fiche produit : seulement la souris qui sort par le haut'
      : `au premier de : ${delai} s, ${defilement} % lus${sortie ? ', souris qui sort par le haut' : ''} (jamais avant 4 s)`);

    if (!surFiche) {
      /* Au-dessus de 990 px, Horizon fige `html` et `body` et fait défiler
         `.page-wrapper` : la fenêtre ne bouge plus et n'émet aucun `scroll`, et ce
         signal ne partait jamais sur ordinateur. On mesure l'élément qui défile
         vraiment et on écoute les deux, comme `akinod-behaviors.js` : la bascule
         se fait au redimensionnement, sans rechargement. */
      const enveloppe = document.querySelector('.page-wrapper');
      const lu = () => {
        const defileur = enveloppe && enveloppe.scrollHeight > enveloppe.clientHeight + 1
          ? enveloppe
          : (document.scrollingElement || document.documentElement);
        const course = defileur.scrollHeight - defileur.clientHeight;

        if (course > 0 && defileur.scrollTop / course >= defilement / 100) declencher('defilement');
      };

      window.addEventListener('scroll', lu, options);
      enveloppe?.addEventListener('scroll', lu, options);
    }

    if (sortie && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('mouseout', (evenement) => {
        if (!evenement.relatedTarget && evenement.clientY <= 0) declencher('sortie');
      }, options);
    }
  }

  /** Un signal est parti : dernier regard sur le panier, puis on attend que la voie soit libre. */
  async tenter(motif, source) {
    const panier = await lirePanier();

    if (panier) {
      this.offreAuPanier = this.offreDejaPrise(this.etatDuPanier(panier));

      if (this.offreAuPanier) {
        this.noter('Offre au panier', false, 'code ou cadeau au panier : elle se tait');
        this.majLanguette();

        return;
      }

      if (!motif && this.seuilPossible(this.etat) && this.atteintLeSeuil(panier)) motif = 'seuil';
    }

    if (!motif) {
      this.noter('Motif', false, 'panier sous le seuil');

      return;
    }

    if (!(await this.voieLibre())) {
      this.noter('Voie libre', false, 'bloquée plus de 60 s : abandon pour cette page');

      return;
    }

    if (this.etat.converti || !this.peutOuvrir(motif)) return;

    await this.precharger();
    this.ouvrir(motif, source);
  }

  /** Dernière vérification avant d'ouvrir : un autre onglet a pu la montrer entre-temps. */
  peutOuvrir(motif) {
    const etat = this.etat;

    if (motif === 'seuil') return !etat.seuil && Date.now() - Math.max(etat.fermeture || 0, etat.derniere || 0) >= RECUL_SEUIL;

    return !lire('sessionStorage', SESSION + this.cfg.campagne);
  }

  /** Résout à `true` quand rien ne s'oppose à l'ouverture, à `false` si on renonce. */
  async voieLibre() {
    const debut = Date.now();
    let libreDepuis = null;
    let bloquee = false;

    while (Date.now() - debut < PATIENCE_MAX) {
      if (this.abandon.signal.aborted) return false;

      const obstacle = (document.hidden && 'onglet en arrière-plan')
        || (bandeauCookiesVisible() && 'bandeau de cookies à l’écran')
        || (autreFenetreOuverte(this.dialogue) && 'panier, menu ou fenêtre ouverts')
        || (saisieEnCours() && 'saisie en cours');

      this.noter('Voie libre', !obstacle, obstacle ? `attend : ${obstacle}` : 'oui');

      if (obstacle) {
        bloquee = true;
        libreDepuis = null;
      } else {
        libreDepuis ??= Date.now();
        if (!bloquee || Date.now() - libreDepuis >= ACCALMIE) return true;
      }

      await attendre(500);
    }

    return false;
  }

  /**
   * Le panier a changé sur la page (ajout, retrait, code saisi). Une offre prise éteint
   * tout ; un seuil tout juste atteint peut rouvrir la pop-up, une fois.
   */
  suivreLePanier() {
    const surChangement = async (evenement) => {
      try {
        await evenement.promise;
      } catch {
        // on relit le panier quand même
      }

      const panier = await lirePanier();

      if (!panier) return;

      this.offreAuPanier = this.offreDejaPrise(this.etatDuPanier(panier));
      this.majLanguette();

      if (this.offreAuPanier) return;

      const etat = this.etat;

      if (etat.converti || this.dialogue.open || !this.dansLaPeriode()) return;
      if (!this.seuilPossible(etat) || !this.atteintLeSeuil(panier)) return;
      if (!(await this.voieLibre())) return;
      if (this.etat.converti || this.dialogue.open || !this.peutOuvrir('seuil')) return;

      await this.precharger();
      this.ouvrir('seuil', 'panier');
    };

    import('@shopify/events')
      .then(({ StandardEvents }) => {
        const options = { signal: this.abandon.signal };
        document.addEventListener(StandardEvents.cartLinesUpdate, surChangement, options);
        document.addEventListener(StandardEvents.cartDiscountUpdate, surChangement, options);
      })
      .catch(() => {
        // Sans le module d'événements, la pop-up relira le panier à la page suivante.
      });
  }

  /* --- Ouverture et fermeture ------------------------------------------------- */

  /** Visuels et polices prêts avant d'ouvrir, pour ne pas voir la pop-up se composer. */
  precharger() {
    this.prechargement ??= (async () => {
      const images = [...this.dialogue.querySelectorAll('img[data-ak-pop-precharger]')];
      // Chaque mise en page nomme les siennes ; Octobre Rose, la première, ne le fait pas.
      const polices = (this.dialogue.dataset.akPopPolices || '400 16px "Glacial Indifference"|700 16px "League Spartan"').split('|');

      images.forEach((image) => { image.loading = 'eager'; });

      const pret = Promise.allSettled([
        ...images.map((image) => image.decode?.() ?? Promise.resolve()),
        ...polices.map((police) => document.fonts?.load?.(police)),
      ]);

      await Promise.race([pret, attendre(2500)]);
    })();

    return this.prechargement;
  }

  /** Au-dessous de la taille de la maquette, la carte se réduit d'un bloc plutôt que de se déformer. */
  ajusterEchelle() {
    const ordinateur = window.matchMedia('(min-width: 750px)').matches;
    const largeur = ordinateur ? 840 : Math.min(window.innerWidth * 0.92, 380);
    const hauteur = ordinateur ? 600 : (largeur * 1620) / 1080;
    const marge = ordinateur ? 40 : 28;
    const echelle = Math.min(1, (window.innerWidth - marge) / largeur, (window.innerHeight - marge) / hauteur);

    this.dialogue.style.setProperty('--ak-pop-echelle', String(Math.max(echelle, 0.5)));
  }

  ouvrir(motif, source) {
    if (this.dialogue.open) return;

    this.ajusterEchelle();
    this.dialogue.showModal();

    if (!document.documentElement.hasAttribute('scroll-lock')) {
      document.documentElement.setAttribute('scroll-lock', '');
      this.verrouPose = true;
    }

    if (this.languette) this.languette.hidden = true;

    const automatique = !['apercu', 'editeur', 'languette'].includes(motif);

    if (automatique) {
      const etat = this.etat;
      const { relanceFinale } = this.cfg;

      this.memoriser({
        vues: etat.vues + 1,
        derniere: Date.now(),
        seuil: etat.seuil || motif === 'seuil',
        // Le motif `finale` ne se produit que passé le jour du dernier rappel
        // (voir `motif()`) : le tester ici doublait la clause qui suit.
        finale: etat.finale || Boolean(relanceFinale && jourAParis() >= relanceFinale),
      });
      // `automatique` a déjà écarté l'éditeur et l'aperçu, qui ouvrent sous leur
      // propre motif — et `memoriser()` les écarte une seconde fois.
      ecrire('sessionStorage', SESSION + this.cfg.campagne, Date.now());
    }

    this.noter('Ouverte', true, `motif ${motif}, déclencheur ${source}`);
    this.mesurer('akinod_popup_affichee', { motif, declencheur: source });
  }

  fermer(raison) {
    if (this.dialogue.open) this.dialogue.close();
    this.leverVerrou();

    if (raison !== 'navigation') {
      if (!this.etat.converti) this.memoriser({ fermeture: Date.now() });
      this.mesurer('akinod_popup_fermee', { raison });
    }

    this.majLanguette();
  }

  leverVerrou() {
    if (!this.verrouPose) return;
    document.documentElement.removeAttribute('scroll-lock');
    this.verrouPose = false;
  }

  /* --- Languette -------------------------------------------------------------- */

  majLanguette() {
    if (!this.languette) return;

    const etat = this.etat;
    // Rien ici sur la case « Afficher la languette » ni sur la taille de l'écran :
    // la section ne rend la languette que si la case est cochée, et la feuille de
    // style la réserve à l'ordinateur (`@media (max-width: 749px)`, sauf classe
    // `--mobile`). Ce calcul refaisait les deux, et la feuille l'emportait.
    //
    // L'éditeur et l'aperçu montrent l'expérience entière : la languette dès que la
    // pop-up est fermée, sans consulter une mémoire qu'ils n'écrivent pas.
    const visible = this.editeur || this.apercu
      ? !this.dialogue.open && !this.languetteMasqueeEnApercu
      : !this.dialogue.open
        && this.dansLaPeriode()
        && etat.vues > 0
        && !etat.converti
        && !this.offreAuPanier
        && !etat.refus;

    this.languette.hidden = !visible;
  }

  /* --- Gestes ----------------------------------------------------------------- */

  brancher() {
    const options = { signal: this.abandon.signal };
    const d = this.dialogue;

    d.querySelectorAll('[data-ak-pop-fermer]').forEach((bouton) => {
      bouton.addEventListener('click', () => this.fermer('croix'), options);
    });

    // Un clic sur le voile : la carte remplit la fenêtre, seul le fond renvoie la fenêtre elle-même.
    d.addEventListener('click', (evenement) => {
      if (evenement.target === d) this.fermer('voile');
    }, options);

    d.addEventListener('cancel', (evenement) => {
      evenement.preventDefault();
      this.fermer('echap');
    }, options);

    // Filet : une fermeture qui ne serait pas passée par nous lève quand même le verrou.
    d.addEventListener('close', () => {
      this.leverVerrou();
      this.majLanguette();
    }, options);

    d.querySelector('[data-ak-pop-bouton]')?.addEventListener('click', () => {
      this.convertir('bouton');
      this.mesurer('akinod_popup_clic');
      this.leverVerrou();
    }, options);

    d.querySelectorAll('[data-ak-pop-code]').forEach((bouton) => {
      const statut = bouton.parentElement?.querySelector('[data-ak-pop-statut]');

      bouton.addEventListener('click', async () => {
        if (!(await copier(bouton.dataset.akPopCode))) return;

        bouton.classList.add('is-copie');
        if (statut) statut.textContent = bouton.dataset.copie || '';
        this.convertir('copie');
        this.mesurer('akinod_popup_code_copie');

        window.clearTimeout(bouton.akPopMinuterie);
        bouton.akPopMinuterie = window.setTimeout(() => {
          bouton.classList.remove('is-copie');
          if (statut) statut.textContent = '';
        }, Number(bouton.dataset.copieDuree) || 2200);
      }, options);
    });

    this.languette?.querySelector('[data-ak-pop-rouvrir]')?.addEventListener('click', async () => {
      await this.precharger();
      this.ouvrir('languette', 'languette');
      this.mesurer('akinod_popup_rouverte');
    }, options);

    this.languette?.querySelector('[data-ak-pop-languette-fermer]')?.addEventListener('click', () => {
      this.memoriser({ refus: true });
      if (this.editeur || this.apercu) this.languetteMasqueeEnApercu = true;
      this.majLanguette();
      this.mesurer('akinod_popup_languette_masquee');
    }, options);

    // Un seul écouteur de redimensionnement : le passage du seuil de 750 px ne
    // s'accompagne jamais d'autre chose que d'un changement de fenêtre, et
    // `ajusterEchelle()` relit le seuil elle-même.
    window.addEventListener('resize', () => {
      if (d.open) this.ajusterEchelle();
    }, { ...options, passive: true });

    // Retour arrière depuis la cible du bouton : la page revient du cache, pop-up ouverte.
    window.addEventListener('pageshow', (evenement) => {
      if (evenement.persisted && d.open) this.fermer('navigation');
    }, options);

    if (this.editeur) {
      document.addEventListener('shopify:section:select', (evenement) => {
        if (evenement.detail?.sectionId !== this.idSection) return;
        ecrire('sessionStorage', EDITEUR_OUVERTE + this.idSection, 1);
        this.precharger().then(() => this.ouvrir('editeur', 'editeur'));
      }, options);

      document.addEventListener('shopify:section:deselect', (evenement) => {
        if (evenement.detail?.sectionId !== this.idSection) return;
        try {
          window.sessionStorage.removeItem(EDITEUR_OUVERTE + this.idSection);
        } catch {
          // rien à nettoyer
        }
        this.fermer('editeur');
      }, options);
    }
  }

  detruire() {
    this.abandon.abort();
    if (this.dialogue.open) this.dialogue.close();
    this.leverVerrou();
  }
}

/* ── Démarrage ────────────────────────────────────────────────────────────────── */

function demarrer(racineSection) {
  const balise = racineSection.querySelector('[data-ak-pop-config]');
  const dialogue = racineSection.querySelector('dialog[data-ak-pop]');

  if (!balise || !dialogue || dialogue.akPop) return;

  try {
    dialogue.akPop = new PopupCampagne(racineSection, dialogue, JSON.parse(balise.textContent || '{}'));
  } catch (erreur) {
    // Une pop-up mal réglée ne doit jamais casser la page — mais elle doit laisser
    // une trace : sans elle, « pourquoi elle ne s'affiche pas » reste indébrouillable.
    console.warn('[akinod-popup]', erreur);
  }
}

function toutDemarrer() {
  document.querySelectorAll('.ak-pop-section').forEach(demarrer);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', toutDemarrer, { once: true });
} else {
  toutDemarrer();
}

// Éditeur de thème : une section rechargée repart de zéro, l'ancienne est démontée.
document.addEventListener('shopify:section:unload', (evenement) => {
  evenement.target?.querySelector?.('dialog[data-ak-pop]')?.akPop?.detruire();
});

document.addEventListener('shopify:section:load', (evenement) => {
  if (evenement.target instanceof HTMLElement && evenement.target.classList.contains('ak-pop-section')) {
    demarrer(evenement.target);
  }
});
